import { PASSWORD_POLICY, passwordProblems, type PasswordPolicy } from "@starsach/shared-types";
import type { AuthAnswer, CognitoApi, UserContextData } from "./cognito-api";
import { CognitoApiError } from "./cognito-api";
import { devicePasswordClaim, deviceSrpStart, type DeviceVerifier } from "./device-verifier";
import type { FreshTokens } from "./auth";
import type { DeviceSlot, RememberedDevice } from "./session-store";

/**
 * Starsach's sign-in, on the Cognito API (ported verbatim from gairm/lms e0d8eb7, gairm/lms#392; crmarr/templates auth-self-hosted-sign-in.md, R1, R2, R8, R10).
 * No React here: the screen (pages/LoginPage.tsx) renders `state` and calls the methods; the tests drive every
 * transition with a fake API.
 *
 *   email ── Continue ──▶ InitiateAuth USER_AUTH (passkey preferred where the browser has WebAuthn)
 *     ├─ WEB_AUTHN ─────────▶ passkey: the browser's prompt ──▶ tokens ──▶ finishing ──▶ done
 *     │                         └─ canceled or refused ──▶ choose (passkey again, or password)
 *     ├─ SELECT_CHALLENGE ──▶ password (WEB_AUTHN on offer too: "Use your passkey instead")
 *     ├─ an error ──────────▶ email, with the error
 *     └─ (an organization's address, before any of these) ──▶ provider: the hosted endpoint, R7
 *   password ── Continue ──▶ InitiateAuth USER_AUTH, PASSWORD
 *     ├─ SOFTWARE_TOKEN_MFA ▶ code ── Continue ──▶ RespondToAuthChallenge ──▶ tokens ──▶ finishing ──▶ done
 *     ├─ NEW_PASSWORD_REQUIRED ▶ new-password (first sign-in, gairm/lms#393) ── Continue ──▶ RespondToAuthChallenge
 *     │                           ├─ MFA_SETUP ─────▶ authenticator (below)
 *     │                           ├─ SOFTWARE_TOKEN_MFA ▶ code (a re-invited person who already has an authenticator)
 *     │                           └─ InvalidPasswordException ─▶ new-password, the rule named; the session stays open
 *     ├─ MFA_SETUP ─────────▶ authenticator: AssociateSoftwareToken with the session ──▶ the secret, as text and as a
 *     │                         QR code ── the code ──▶ VerifySoftwareToken ──▶ RespondToAuthChallenge MFA_SETUP ──▶
 *     │                         tokens ──▶ finishing ──▶ passkey-offer (add one now, or skip) ──▶ done
 *     │                           a passkey closed, refused, unsupported or failed stays on the offer, said in words;
 *     │                           Not now always goes in — even while the browser's prompt is open, which it closes
 *     └─ anything else ─────▶ password, "the email or password is not right" (never which)
 *   password, on a remembered device (gairm/lms#400): InitiateAuth carries its DEVICE_KEY, and Cognito answers
 *     DEVICE_SRP_AUTH in place of the code ── SRP_A ──▶ DEVICE_PASSWORD_VERIFIER ── the device's proof ──▶ tokens ──▶ in
 *       (no code). A key Cognito no longer knows (ResourceNotFoundException), or a proof it refuses: the remembered
 *       device is forgotten here and the same password is sent again without it, so the person meets the code step
 *       and nothing else. Proven on lms-dev under USER_AUTH, 2026-10-02.
 *   step-up ("Confirm it's you", gairm/lms#397; R6: one ceremony): the same machine, opened by StepUpProvider.tsx on the
 *     signed-in person's username, at the password step with the passkey on offer beside it. The same passkey, password,
 *     code and device-proof steps and the same words; every call carries the step-up's ClientMetadata (R12). The
 *     session's own device key is sent with the password, so a remembered device proves itself as it does at sign-in and
 *     a confirmed one is answered with the code — and Cognito issues no new device. On tokens: nothing is confirmed,
 *     remembered, offered or verified — they go straight to the session layer (auth.ts replaceTokens), which keeps the
 *     session's own refresh token and device. Never the email step, never a first sign-in's challenges.
 *   finishing: ConfirmDevice for the device Cognito issued (device-verifier.ts), so the refresh token renews; with
 *   "Remember this device" ticked (the code and authenticator steps), UpdateDeviceStatus remembered and the device kept
 *   for this address (session-store.ts) — best effort: a refusal leaves the sign-in as it is, asking for the code next
 *   time; after a
 *   first sign-in's authenticator setup, the TOTP preference recorded (pitfall 5: the MFA_SETUP answer leaves it unset,
 *   measured on lms-dev 2026-10-01); then the tokens and the device slot go to the session layer — after the passkey
 *   offer, when there is one, because handing them over leaves this screen.
 *
 * R10: an address that has no account is never named. Cognito keeps `preventUserExistenceErrors` on, and its answers for
 * an unknown address imitate a person with a passkey (SELECT_CHALLENGE offering WEB_AUTHN; a password answered with
 * SELECT_CHALLENGE rather than an error — measured on lms-dev, 2026-10-01); every one of them reads here as the same
 * words a wrong password gets.
 */

export type Factor = "WEB_AUTHN" | "PASSWORD";

export type Step =
  | { name: "email" }
  | { name: "passkey" }
  | { name: "choose" }
  | { name: "password" }
  | { name: "code"; session: string }
  | { name: "new-password"; session: string }
  /** `session` and `secret` arrive together from AssociateSoftwareToken; until then the step is loading. */
  | { name: "authenticator"; session?: string; secret?: string }
  /** `slow` while a device confirmation Cognito has not answered yet is waited for (gairm/lms#431): the screen says so. */
  | { name: "finishing"; slow?: boolean }
  /** `adding` while the browser's prompt is open: Not now still answers, and closes it. */
  | { name: "passkey-offer"; adding?: boolean }
  /** Forgot your password? (gairm/lms#394): the address, then the emailed code with the new password. */
  | { name: "forgot" }
  | { name: "reset" }
  /** A signed-in person whose address is not verified (gairm/lms#394): the emailed code, or Not now. */
  | { name: "verify-email"; sent: boolean }
  | { name: "provider" }
  | { name: "done" };

/** Good news worth saying once, as it happens (gairm/lms#394); cleared with the person's next move. */
export type SignInNotice = "code-resent" | "password-changed";

export const SIGN_IN_NOTICE_COPY: Record<SignInNotice, string> = {
  "code-resent": "We've sent a new code. Use the one in the newest email.",
  "password-changed": "Your password is changed, and you're signed out on your other devices. Sign in with your new password.",
};

export type SignInErrorCode =
  | "email-missing"
  | "password-missing"
  | "code-format"
  | "wrong-password"
  | "wrong-code"
  | "code-used"
  | "expired"
  | "locked"
  | "rate-limited"
  | "reset-required"
  | "not-confirmed"
  | "disabled"
  | "passkey-canceled"
  | "passkey-failed"
  | "password-rules"
  | "password-rejected"
  | "temp-expired"
  | "first-expired"
  | "setup-expired"
  | "passkey-not-added"
  | "passkey-offer-canceled"
  | "passkey-unsupported"
  | "email-code-format"
  | "email-code-wrong"
  | "email-code-expired"
  | "code-rate-limited"
  | "network"
  | "device"
  | "unexpected";

export interface SignInState {
  step: Step;
  /** The address as typed, trimmed and lower-cased: Starsach creates every person lower-case (AdminCreateUser on the trimmed, lower-cased address). */
  email: string;
  /** The factors this person may use, as Cognito last said; both until it says otherwise. */
  factors: Factor[];
  busy: boolean;
  /** "Remember this device" (gairm/lms#400), ticked on the code or authenticator step; off until the person ticks it. */
  remember: boolean;
  error?: SignInErrorCode;
  notice?: SignInNotice;
}

/** What the session layer is handed once the sign-in is complete. */
export interface SignedIn {
  tokens: FreshTokens;
  device?: DeviceSlot;
  /** "Remember this device" was ticked and held in this sign-in: the screen tells the trail (gairm/lms#421). */
  rememberedNow?: boolean;
}

export interface SignInDeps {
  api: Pick<
    CognitoApi,
    | "startUserAuth"
    | "respondToChallenge"
    | "confirmDevice"
    | "getTokensFromRefreshToken"
    | "updateDeviceStatus"
    | "associateSoftwareTokenInSignIn"
    | "verifySoftwareTokenInSignIn"
    | "setTotpPreference"
    | "forgotPassword"
    | "confirmForgotPassword"
    | "sendEmailVerificationCode"
    | "verifyEmail"
  >;
  /**
   * Whether the sign-in's address is verified (the ID token's `email_verified`; gairm/lms#394). Absent, it is read from the
   * ID token; a token that cannot be read counts as verified, so a mock or odd token never blocks a sign-in.
   */
  emailVerified?: (tokens: FreshTokens, email: string) => boolean;
  /** The pool's password policy (config.json's, from the object the pool is built from); the shared one when absent. */
  passwordPolicy?: PasswordPolicy;
  /**
   * First sign-in's passkey offer (gairm/lms#393): Account › Security's enrollment (passkey-enroll.ts) with the new
   * sign-in's access token; `signal` closes the browser's prompt (Not now while it is open). Absent, or no WebAuthn
   * here, and there is no offer.
   */
  enrollPasskey?: (accessToken: string, signal?: AbortSignal) => Promise<void>;
  /** WebAuthn exists in this browser (cognito-api.ts passkeysPossible). */
  passkeysPossible: () => boolean;
  /** The passkey ceremony (cognito-api.ts passkeyAssertion): Cognito's request options in, the assertion JSON out. */
  passkeyAssertion: (requestOptionsJson: string | undefined, signal?: AbortSignal) => Promise<string>;
  /** R8: the advanced-security data for this person, or undefined when the browser could not produce it. */
  contextData: (username: string) => Promise<UserContextData | undefined>;
  deviceVerifier: (groupKey: string, deviceKey: string) => Promise<DeviceVerifier>;
  /** The name Cognito lists the device under (Account › Security will show it, gairm/lms#400). */
  deviceName: () => string;
  /**
   * This browser's remembered devices, by address (session-store.ts; gairm/lms#400). Absent, nothing is remembered here
   * and "Remember this device" only marks the device at Cognito.
   */
  rememberedDevices?: {
    read: (email: string) => RememberedDevice | undefined;
    write: (email: string, device: RememberedDevice) => void;
    remove: (email: string) => void;
  };
  /** Hands the tokens and the device to the session layer. */
  complete: (result: SignedIn) => Promise<void>;
  /** Waits between the renewals that check a device confirmation Cognito has not answered yet (gairm/lms#431); tests pass their own. */
  sleep?: (ms: number) => Promise<void>;
  /**
   * Step-up (gairm/lms#397): the signed-in person, by the username Cognito knows them by (the ID token's
   * `cognito:username`). The machine opens at the password step for them, and its tokens go to `complete` untouched:
   * no ConfirmDevice, no Remember this device, no passkey offer, no address check. `rememberedDevices.read` answers with
   * the session's own device, so its key goes with the password.
   */
  stepUp?: { username: string };
  /** Sent as ClientMetadata with every InitiateAuth and RespondToAuthChallenge (R12): step-up's STEP_UP_METADATA. */
  clientMetadata?: Readonly<Record<string, string>>;
  /**
   * R7, email-first routing (gairm/lms#398): true when this address belongs to an organization that signs in through its
   * own identity provider and the browser is leaving for it. Nothing on the screen names which organizations exist.
   */
  toProvider?: (email: string) => Promise<boolean>;
}

/**
 * Starsach's own words for each error (R10; gairm/lms#428): plain, short, what to do next, no blame, never whether an
 * address has an account, and one term for each thing (design.md §6, "The words on the sign-in screens").
 */
export const SIGN_IN_ERROR_COPY: Record<SignInErrorCode, string> = {
  "email-missing": "Enter your email address, like name@example.org.",
  "password-missing": "Enter your password.",
  "code-format": "Enter the six digits from your authenticator app.",
  // Also an unknown address, a disabled account and an unconfirmed one (R10): the same words, so none of them is told apart.
  "wrong-password": "That email address and password don't match. Check both and try again. Still stuck? Choose Forgot your password? or Trouble signing in? below.",
  "wrong-code": "That code didn't work. Codes change every 30 seconds, so enter the one your authenticator app shows now.",
  "code-used": "That code has already been used. Wait for the next one in your authenticator app, then enter it.",
  expired: "That took a little too long, so the sign-in timed out. Nothing was changed. Enter your password again.",
  // Cognito's lockout starts short and doubles with each failure, up to about fifteen minutes.
  locked: "Signing in is paused after too many tries. Wait 15 minutes, then try again. If you've forgotten your password, use Trouble signing in? below.",
  "rate-limited": "There are too many sign-in attempts just now. Wait a minute, then try again. Nothing was changed.",
  "reset-required": "Your password needs to be reset before you can sign in. Choose Forgot your password? below, and we'll email you a code to reset it.",
  "not-confirmed": "You can't sign in yet. Use Trouble signing in? below, or ask your organization's managers.",
  disabled: "You can't sign in right now. Use Trouble signing in? below, or ask your organization's managers.",
  "passkey-canceled": "The passkey prompt was closed before it finished. Try your passkey again, or use your password.",
  "passkey-failed": "Your passkey didn't work this time. Try it again, or use your password.",
  "password-rules": "Your new password doesn't meet every rule yet. Check the rules above the field.",
  "password-rejected": "That password can't be used. It may be one that has appeared in a data breach. Choose a different one.",
  "temp-expired": "Your temporary password has expired. Ask one of your organization's managers to send your invitation again, or use Trouble signing in? below. It comes with a new temporary password.",
  "first-expired": "That took a little too long, so the sign-in timed out. Nothing was changed. Enter your temporary password again.",
  "setup-expired": "That took a little too long, so the setup timed out. Your new password is saved. Enter it to finish setting up your authenticator app.",
  // The offer's three (gairm/lms#393): each says what happened and that Not now goes on, so no one is left at the offer.
  "passkey-not-added": "The passkey wasn't added because something went wrong while saving it. Choose Add a passkey to try again, or Not now to go on. You can add one later from Account, Security.",
  "passkey-offer-canceled": "The passkey prompt was closed, so no passkey was added. Choose Add a passkey to try again, or Not now to go on. You can add one later from Account, Security.",
  "passkey-unsupported": "This browser or device can't save a passkey. Choose Not now to go on. You can add one later from Account, Security, on a device that can.",
  // The screen above the field names the email by its subject (Starsach's own, or the pool's where Cognito still sends;
  // gairm/lms#410, R9), so the field's error need not repeat it.
  "email-code-format": "Enter the six-digit code from the email. It's on a line by itself.",
  "email-code-wrong": "That code didn't match. Find the newest email with your code and enter that code.",
  "email-code-expired": "That code has expired or has already been used. Choose Send a new code, then use the code in the newest email.",
  "code-rate-limited": "Too many codes were asked for or tried just now. Wait 15 minutes, then try again. If it still doesn't work, use Trouble signing in? below.",
  network: "We couldn't reach the sign-in service. Check your connection, then try again. Nothing was changed.",
  device: "We couldn't finish signing you in on this device. Nothing was changed. Try signing in again.",
  unexpected: "Something went wrong while signing you in. Nothing was changed. Try again. If it keeps happening, use Trouble signing in? below.",
};

/**
 * Confirm it's you (gairm/lms#397) runs the same machine for a person who is already signed in, so where the sign-in's
 * words speak of an email address or a sign-in, the dialog's speak of the password and of confirming. Nothing here can
 * tell an address apart: the dialog never asks for one.
 */
export const STEP_UP_ERROR_COPY: Partial<Record<SignInErrorCode, string>> = {
  "wrong-password": "That password isn't right. Check it and try again. If you've forgotten it, use Trouble signing in? below.",
  expired: "That took a little too long, so confirming timed out. Nothing was changed. Enter your password again.",
  unexpected: "Something went wrong while confirming it's you. Nothing was changed. Try again. If it keeps happening, use Trouble signing in? below.",
};

/** The words for an error, as the sign-in screen or Confirm it's you says them. */
export function signInErrorWords(code: SignInErrorCode, stepUp = false): string {
  return (stepUp ? STEP_UP_ERROR_COPY[code] : undefined) ?? SIGN_IN_ERROR_COPY[code];
}

/** Why the offer's enrollment added no passkey, as the offer says it: closed, not possible here, or failed. */
export function classifyEnrollment(err: unknown): SignInErrorCode {
  const name = (err as { name?: unknown } | null)?.name;
  if (name === "NotAllowedError" || name === "AbortError") return "passkey-offer-canceled";
  if (name === "NotSupportedError") return "passkey-unsupported";
  return "passkey-not-added";
}

/**
 * Cognito's error, or the browser's, as one of ours. R10: at the email and password steps, a disabled account and an
 * unconfirmed one read as a wrong password, as an unknown address does, so the words never say an address has an account
 * (gairm/lms#428). Past the password (the code, the setup, a passkey), the person has proved who they are and is told.
 */
export function classify(err: unknown, during: "start" | "password" | "code" | "passkey" | "setup"): SignInErrorCode {
  if (err instanceof CognitoApiError) {
    const msg = err.message.toLowerCase();
    switch (err.code) {
      case "NotAuthorizedException":
        if (msg.includes("attempts exceeded") || msg.includes("temporarily locked") || msg.includes("too many")) return "locked";
        // "Temporary password has expired and must be reset by an administrator." (gairm/lms#393)
        if (msg.includes("temporary password has expired")) return "temp-expired";
        if (msg.includes("session")) return "expired";
        if (msg.includes("disabled")) return during === "start" || during === "password" ? "wrong-password" : during === "passkey" ? "passkey-failed" : "disabled";
        return during === "code" ? "wrong-code" : during === "passkey" ? "passkey-failed" : during === "setup" ? "unexpected" : "wrong-password";
      case "UserNotFoundException":
        return "wrong-password";
      // EnableSoftwareTokenMFAException: VerifySoftwareToken's word for a wrong first code (lms-dev, 2026-10-01).
      case "CodeMismatchException":
      case "EnableSoftwareTokenMFAException":
        return "wrong-code";
      case "InvalidPasswordException":
        return "password-rejected";
      case "ExpiredCodeException":
        // Cognito's word for a one-time code already spent in its 30-second step.
        return msg.includes("used") ? "code-used" : "expired";
      case "PasswordResetRequiredException":
        return "reset-required";
      case "UserNotConfirmedException":
        return during === "start" || during === "password" ? "wrong-password" : "not-confirmed";
      case "TooManyRequestsException":
      case "LimitExceededException":
      case "TooManyFailedAttemptsException":
        return "rate-limited";
      case "TimeoutError":
        return "network";
      default:
        return "unexpected";
    }
  }
  if (err instanceof DOMException || (err as { name?: unknown } | null)?.name === "NotAllowedError") {
    const name = (err as { name?: string }).name;
    return name === "NotAllowedError" || name === "AbortError" ? "passkey-canceled" : "passkey-failed";
  }
  // fetch's own failure: offline, DNS, a blocked request.
  if (err instanceof TypeError) return "network";
  return during === "passkey" ? "passkey-failed" : "unexpected";
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;

export class SignInMachine {
  private s: SignInState = { step: { name: "email" }, email: "", factors: ["WEB_AUTHN", "PASSWORD"], busy: false, remember: false };
  private readonly listeners = new Set<(s: SignInState) => void>();
  /** Bumped on every restart, so an answer that arrives for a sign-in the person has left is dropped. */
  private generation = 0;
  /** The browser's passkey prompt while it is open, so "Use your password instead" can close it. */
  private prompt: AbortController | undefined;
  /** First sign-in: Cognito took the new password, so a timeout from here asks for that one, not the temporary one. */
  private newPasswordSet = false;
  /** First sign-in: this sign-in set up the authenticator, so it records the preference and offers a passkey. */
  private authenticatorSetUp = false;
  /** The finished sign-in, held while the passkey offer is on screen: handing it over leaves this screen. */
  private pending: SignedIn | undefined;
  /** The offer's enrollment while the browser's prompt is open, so Not now can close it and go in. */
  private enrolling: AbortController | undefined;
  /**
   * The device this sign-in sent the key of (gairm/lms#400): Cognito issues no new one, so it is the session's device.
   * `deviceRemembered` when it proved itself; false when Cognito took the key but asked for the code all the same (the
   * device is confirmed but no longer remembered).
   */
  private device: RememberedDevice | undefined;
  private deviceRemembered = false;

  constructor(private readonly deps: SignInDeps) {
    if (deps.stepUp) this.s = { ...this.s, step: { name: "password" }, email: deps.stepUp.username };
  }

  /** Where the machine starts and falls back to: the email step, or a step-up's password step. */
  private home(): Step {
    return this.deps.stepUp ? { name: "password" } : { name: "email" };
  }

  /** The step-up's ClientMetadata, as the API client takes it. */
  private meta(): { clientMetadata?: Record<string, string> } {
    return this.deps.clientMetadata ? { clientMetadata: { ...this.deps.clientMetadata } } : {};
  }

  /** RespondToAuthChallenge, with the step-up's ClientMetadata when there is one (and no trailing argument when not). */
  private respond(name: string, session: string, responses: Record<string, string>, contextData: UserContextData | undefined): Promise<AuthAnswer> {
    const meta = this.deps.clientMetadata;
    return meta ? this.deps.api.respondToChallenge(name, session, responses, contextData, { ...meta }) : this.deps.api.respondToChallenge(name, session, responses, contextData);
  }

  get state(): SignInState {
    return this.s;
  }

  subscribe(listener: (s: SignInState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Every change clears the last error unless it brings its own: an error stands until the person's next move. */
  private set(next: Partial<SignInState>): void {
    const { error: _previous, notice: _said, ...rest } = this.s;
    const merged: SignInState = { ...rest, ...next };
    if (merged.error === undefined) delete merged.error;
    if (merged.notice === undefined) delete merged.notice;
    this.s = merged;
    for (const l of this.listeners) l(this.s);
  }

  /** Back to the email step: "Use a different email". */
  restart(): void {
    this.closePrompt();
    this.generation++;
    this.newPasswordSet = false;
    this.authenticatorSetUp = false;
    this.pending = undefined;
    this.device = undefined;
    this.deviceRemembered = false;
    this.set({ step: this.home(), busy: false, factors: ["WEB_AUTHN", "PASSWORD"], remember: false });
  }

  /** "Remember this device", ticked or not. Leaves any error standing: a checkbox is not an answer to it. */
  setRemember(on: boolean): void {
    if (this.s.remember === on) return;
    this.s = { ...this.s, remember: on };
    for (const l of this.listeners) l(this.s);
  }

  async submitEmail(typed: string): Promise<void> {
    if (this.s.busy) return;
    const email = typed.trim().toLowerCase();
    if (!EMAIL_SHAPE.test(email)) return this.set({ email: typed.trim(), error: "email-missing" });
    const gen = this.generation;
    this.set({ email, busy: true });
    const passkeys = this.deps.passkeysPossible();
    try {
      if (this.deps.toProvider && (await this.deps.toProvider(email))) {
        if (gen === this.generation) this.set({ busy: true, step: { name: "provider" } });
        return;
      }
      const answer = await this.deps.api.startUserAuth({ username: email, ...(passkeys ? { preferred: "WEB_AUTHN" as const } : {}), ...(await this.context(email)) });
      if (gen !== this.generation) return;
      if (answer.ChallengeName === "WEB_AUTHN" && answer.Session) return await this.runPasskey(answer, gen);
      const factors = factorsOf(answer);
      // No passkey on offer, or none possible in this browser: the password, which every person has.
      this.set({ factors, busy: false, step: { name: "password" } });
    } catch (err) {
      if (gen !== this.generation) return;
      const error = classify(err, "start");
      // R10 (gairm/lms#428): an address Cognito refuses at the first step for its account's state (disabled, unconfirmed)
      // goes on to the password, as an unknown address does; the password step then says what a wrong password gets.
      if (error === "wrong-password") return this.set({ busy: false, step: { name: "password" } });
      // A password that must be reset: said where Forgot your password? is, on the password step.
      if (error === "reset-required") return this.set({ busy: false, step: { name: "password" }, error });
      this.set({ busy: false, step: { name: "email" }, error });
    }
  }

  /** From the choice, or "Use your passkey instead" on the password step: a fresh passkey challenge. */
  async usePasskey(): Promise<void> {
    if (this.s.busy) return;
    const gen = this.generation;
    this.set({ busy: true, step: { name: "passkey" } });
    try {
      const answer = await this.deps.api.startUserAuth({ username: this.s.email, preferred: "WEB_AUTHN", ...(await this.context(this.s.email)), ...this.meta() });
      if (gen !== this.generation) return;
      if (answer.ChallengeName === "WEB_AUTHN" && answer.Session) return await this.runPasskey(answer, gen);
      // Cognito no longer offers a passkey for this person: the password.
      this.set({ busy: false, factors: factorsOf(answer), step: { name: "password" } });
    } catch (err) {
      if (gen !== this.generation) return;
      this.set({ busy: false, step: { name: "choose" }, error: classify(err, "passkey") });
    }
  }

  /**
   * From the choice, or from the passkey step while the browser's prompt is still open: the password instead. The prompt
   * is closed and whatever it would have answered is dropped.
   */
  usePassword(): void {
    if (this.s.step.name === "passkey") {
      this.closePrompt();
      this.generation++;
      return this.set({ busy: false, step: { name: "password" } });
    }
    if (this.s.busy) return;
    this.set({ step: { name: "password" } });
  }

  private closePrompt(): void {
    this.prompt?.abort();
    this.prompt = undefined;
    this.enrolling?.abort();
    this.enrolling = undefined;
  }

  async submitPassword(password: string): Promise<void> {
    if (this.s.busy) return;
    if (!password) return this.set({ error: "password-missing" });
    const gen = this.generation;
    this.set({ busy: true });
    const email = this.s.email;
    const start = async (deviceKey?: string) => this.deps.api.startUserAuth({ username: email, preferred: "PASSWORD", password, ...(deviceKey ? { deviceKey } : {}), ...(await this.context(email)), ...this.meta() });
    try {
      let remembered = this.readRemembered(email);
      let answer: AuthAnswer;
      try {
        answer = await start(remembered?.key);
      } catch (err) {
        // Forgotten at Cognito since (Sign out everywhere, or Remove on another browser): forgotten here too, and the
        // same password sent again without it.
        if (!remembered || !(err instanceof CognitoApiError) || err.code !== "ResourceNotFoundException") throw err;
        if (gen !== this.generation) return;
        this.forgetRemembered(email);
        remembered = undefined;
        answer = await start();
      }
      if (gen !== this.generation) return;
      // The session is passed on when Cognito sends one; the handshake is taken without one too (proveDevice), so an
      // answer without it is still the device's step and never a wrong password.
      if (answer.ChallengeName === "DEVICE_SRP_AUTH" && remembered) {
        const proven = await this.proveDevice(answer.Session ?? "", remembered);
        if (gen !== this.generation) return;
        if (proven) {
          this.device = remembered;
          this.deviceRemembered = true;
          return await this.afterFirstFactor(proven, gen);
        }
        // Refused: the device can no longer prove itself, so it is not remembered here; the code step instead.
        this.forgetRemembered(email);
        answer = await start();
        if (gen !== this.generation) return;
      } else if (remembered && answer.ChallengeName) {
        // The key was taken, but Cognito asks for the code: the device is confirmed and no longer remembered there. It is
        // still this sign-in's device (the refresh token is bound to it), and no longer remembered here either.
        this.forgetRemembered(email);
        this.device = remembered;
        this.deviceRemembered = false;
      }
      await this.afterFirstFactor(answer, gen);
    } catch (err) {
      if (gen !== this.generation) return;
      this.set({ busy: false, step: { name: "password" }, error: classify(err, "password") });
    }
  }

  /**
   * The remembered device's handshake (device-verifier.ts): DEVICE_SRP_AUTH with SRP_A, then DEVICE_PASSWORD_VERIFIER with
   * the proof. Cognito's answer to the proof (tokens, or a further challenge), or undefined when Cognito refused the
   * device. A network failure or a timeout is thrown: the device is not to blame for it.
   */
  private async proveDevice(session: string, device: RememberedDevice): Promise<AuthAnswer | undefined> {
    const username = this.s.email;
    const contextData = (await this.context(username)).contextData;
    try {
      const start = deviceSrpStart();
      const srp = await this.respond("DEVICE_SRP_AUTH", session, { USERNAME: username, DEVICE_KEY: device.key, SRP_A: start.srpA }, contextData);
      const p = srp.ChallengeParameters ?? {};
      const srpB = p["SRP_B"];
      const salt = p["SALT"];
      const secretBlock = p["SECRET_BLOCK"];
      // Cognito answers DEVICE_SRP_AUTH with no Session (lms-dev, 2026-10-02); the proof is taken without one.
      if (srp.ChallengeName !== "DEVICE_PASSWORD_VERIFIER" || !srpB || !salt || !secretBlock) return undefined;
      const claim = await devicePasswordClaim({ groupKey: device.groupKey, deviceKey: device.key, password: device.password, start, srpB, salt, secretBlock });
      return await this.respond("DEVICE_PASSWORD_VERIFIER", srp.Session ?? "", { USERNAME: p["USERNAME"] ?? username, DEVICE_KEY: device.key, ...claim }, contextData);
    } catch (err) {
      if (err instanceof TypeError || (err instanceof CognitoApiError && err.code === "TimeoutError")) throw err;
      return undefined;
    }
  }

  private readRemembered(email: string): RememberedDevice | undefined {
    try {
      return this.deps.rememberedDevices?.read(email);
    } catch {
      return undefined;
    }
  }

  private forgetRemembered(email: string): void {
    try {
      this.deps.rememberedDevices?.remove(email);
    } catch {
      // No storage: nothing was kept.
    }
  }

  async submitCode(typed: string): Promise<void> {
    const step = this.s.step;
    if (this.s.busy || step.name !== "code") return;
    // Spaces and dashes are how a code is often pasted or read out: they are not part of it.
    const code = typed.replace(/[\s-]/g, "");
    if (!/^\d{6}$/.test(code)) return this.set({ error: "code-format" });
    const gen = this.generation;
    this.set({ busy: true });
    try {
      const answer = await this.respond("SOFTWARE_TOKEN_MFA", step.session, { USERNAME: this.s.email, SOFTWARE_TOKEN_MFA_CODE: code }, (await this.context(this.s.email)).contextData);
      if (gen !== this.generation) return;
      if (answer.AuthenticationResult) return await this.finish(answer.AuthenticationResult, gen);
      this.set({ busy: false, error: "unexpected" });
    } catch (err) {
      if (gen !== this.generation) return;
      const error = classify(err, "code");
      // A wrong or spent code leaves the challenge open (measured on lms-dev: the same session takes the right code after
      // a wrong one), and so does a network failure; an expired session, a lockout or anything else does not, and the
      // person starts again from the password.
      if (error === "wrong-code" || error === "code-used" || error === "network") this.set({ busy: false, error });
      else this.set({ busy: false, step: { name: "password" }, error });
    }
  }

  /**
   * First sign-in: the person's own password in place of the temporary one (gairm/lms#393). Every rule is checked here
   * first, in the policy's words, so Cognito is asked only with a password it will take; its own refusal (a password
   * found in a breach, under threat protection) leaves the challenge open (measured on lms-dev: the session takes a
   * good password after a refused one).
   */
  async submitNewPassword(password: string): Promise<void> {
    const step = this.s.step;
    if (this.s.busy || step.name !== "new-password") return;
    if (!password) return this.set({ error: "password-missing" });
    if (passwordProblems(password, this.deps.passwordPolicy ?? PASSWORD_POLICY).length > 0) return this.set({ error: "password-rules" });
    const gen = this.generation;
    this.set({ busy: true });
    try {
      const answer = await this.deps.api.respondToChallenge("NEW_PASSWORD_REQUIRED", step.session, { USERNAME: this.s.email, NEW_PASSWORD: password }, (await this.context(this.s.email)).contextData);
      if (gen !== this.generation) return;
      this.newPasswordSet = true;
      await this.afterFirstFactor(answer, gen);
    } catch (err) {
      if (gen !== this.generation) return;
      const error = classify(err, "setup");
      if (error === "password-rejected" || error === "network") return this.set({ busy: false, error });
      // A timed-out challenge: nothing changed, so the temporary password is still the one to type.
      this.set({ busy: false, step: { name: "password" }, error: error === "expired" ? "first-expired" : error });
    }
  }

  /** MFA_SETUP: a secret for this person's authenticator, against the challenge's session (no access token yet). */
  private async associate(session: string, gen: number): Promise<void> {
    this.set({ busy: true, step: { name: "authenticator" } });
    try {
      const r = await this.deps.api.associateSoftwareTokenInSignIn(session);
      if (gen !== this.generation) return;
      if (!r.SecretCode || !r.Session) return this.toPasswordAfterSetupFailure("unexpected");
      this.set({ busy: false, step: { name: "authenticator", session: r.Session, secret: r.SecretCode } });
    } catch (err) {
      if (gen !== this.generation) return;
      this.toPasswordAfterSetupFailure(classify(err, "setup"));
    }
  }

  /** The authenticator's first code: VerifySoftwareToken, then the MFA_SETUP challenge answered — and in. */
  async submitSetupCode(typed: string): Promise<void> {
    const step = this.s.step;
    if (this.s.busy || step.name !== "authenticator" || !step.session) return;
    const code = typed.replace(/[\s-]/g, "");
    if (!/^\d{6}$/.test(code)) return this.set({ error: "code-format" });
    const gen = this.generation;
    this.set({ busy: true });
    let verified: { Status?: string; Session?: string };
    try {
      verified = await this.deps.api.verifySoftwareTokenInSignIn(step.session, code, "Authenticator app");
    } catch (err) {
      if (gen !== this.generation) return;
      const error = classify(err, "code");
      // A wrong code leaves the association open (lms-dev, 2026-10-01), so the person types the next one.
      if (error === "wrong-code" || error === "code-used" || error === "network") return this.set({ busy: false, error });
      return this.toPasswordAfterSetupFailure(error);
    }
    if (gen !== this.generation) return;
    if (verified.Status !== "SUCCESS" || !verified.Session) return this.set({ busy: false, error: "wrong-code" });
    this.authenticatorSetUp = true;
    try {
      const answer = await this.deps.api.respondToChallenge("MFA_SETUP", verified.Session, { USERNAME: this.s.email }, (await this.context(this.s.email)).contextData);
      if (gen !== this.generation) return;
      await this.afterFirstFactor(answer, gen);
    } catch (err) {
      if (gen !== this.generation) return;
      this.toPasswordAfterSetupFailure(classify(err, "setup"));
    }
  }

  /** The setup could not go on: back to the password, worded for which password is now the person's. */
  private toPasswordAfterSetupFailure(error: SignInErrorCode): void {
    const expired = error === "expired";
    this.set({ busy: false, step: { name: "password" }, error: expired ? (this.newPasswordSet ? "setup-expired" : "first-expired") : error });
  }

  /**
   * The passkey offer after first sign-in: Account › Security's enrollment, then in. A passkey closed, unsupported or
   * failed stays on the offer, in words that say which and that Not now goes on.
   */
  async addPasskey(): Promise<void> {
    const pending = this.pending;
    if (this.s.busy || this.s.step.name !== "passkey-offer" || !pending) return;
    if (!this.deps.enrollPasskey) return this.set({ error: "passkey-unsupported" });
    const gen = this.generation;
    const enrolling = new AbortController();
    this.enrolling = enrolling;
    this.set({ busy: true, step: { name: "passkey-offer", adding: true } });
    try {
      await this.deps.enrollPasskey(pending.tokens.AccessToken ?? "", enrolling.signal);
    } catch (err) {
      // Not now closed the prompt and is going in, or the person left for another email: nothing more to say here.
      if (gen !== this.generation || enrolling.signal.aborted) return;
      this.enrolling = undefined;
      return this.set({ busy: false, step: { name: "passkey-offer" }, error: classifyEnrollment(err) });
    }
    if (gen !== this.generation || enrolling.signal.aborted) return;
    this.enrolling = undefined;
    await this.handOver(pending, gen);
  }

  /**
   * "Not now": in, with the authenticator alone; a passkey can be added later on Account › Security. It always answers
   * on the offer — after a passkey that was not added, and while the browser's prompt is still open, which it closes.
   */
  async skipPasskey(): Promise<void> {
    const pending = this.pending;
    const step = this.s.step;
    if (step.name !== "passkey-offer" || !pending || (this.s.busy && !step.adding)) return;
    const gen = this.generation;
    this.enrolling?.abort();
    this.enrolling = undefined;
    this.set({ busy: true, step: { name: "passkey-offer" } });
    await this.handOver(pending, gen);
  }

  /** *Forgot your password?*: the address step of the reset, the address carried over from sign-in (gairm/lms#394). */
  forgotPassword(): void {
    this.closePrompt();
    this.generation++;
    this.newPasswordSet = false;
    this.authenticatorSetUp = false;
    this.pending = undefined;
    this.device = undefined;
    this.deviceRemembered = false;
    this.set({ busy: false, step: { name: "forgot" } });
  }

  /**
   * ForgotPassword, then the code step — whatever Cognito knows about the address (R10). An address with no account is
   * answered like one with an account (preventUserExistenceErrors); an account with no verified address is refused by
   * Cognito with InvalidParameterException, and that too reads as sent, because saying otherwise would say the account
   * exists; the code step says what to do when no code comes.
   */
  async submitForgot(typed: string): Promise<void> {
    if (this.s.busy) return;
    const email = typed.trim().toLowerCase();
    if (!EMAIL_SHAPE.test(email)) return this.set({ email: typed.trim(), error: "email-missing" });
    const gen = this.generation;
    this.set({ email, busy: true });
    const error = await this.requestResetCode(email);
    if (gen !== this.generation) return;
    if (error) return this.set({ busy: false, error });
    this.set({ busy: false, step: { name: "reset" } });
  }

  /** "Send a new code" on the code step. */
  async resendResetCode(): Promise<void> {
    if (this.s.busy || this.s.step.name !== "reset") return;
    const gen = this.generation;
    this.set({ busy: true });
    const error = await this.requestResetCode(this.s.email);
    if (gen !== this.generation) return;
    this.set({ busy: false, ...(error ? { error } : { notice: "code-resent" as const }) });
  }

  private async requestResetCode(email: string): Promise<SignInErrorCode | undefined> {
    try {
      await this.deps.api.forgotPassword(email, (await this.context(email)).contextData);
      return undefined;
    } catch (err) {
      const code = err instanceof CognitoApiError ? err.code : "";
      if (code === "UserNotFoundException" || code === "InvalidParameterException" || code === "NotAuthorizedException" || code === "UserNotConfirmedException") return undefined;
      return classifyEmailCode(err);
    }
  }

  /** The code from the email and the new password: ConfirmForgotPassword, then back to sign-in, said. */
  async submitReset(typedCode: string, password: string): Promise<void> {
    if (this.s.busy || this.s.step.name !== "reset") return;
    const code = typedCode.replace(/[\s-]/g, "");
    if (!/^\d{6}$/.test(code)) return this.set({ error: "email-code-format" });
    if (!password) return this.set({ error: "password-missing" });
    if (passwordProblems(password, this.deps.passwordPolicy ?? PASSWORD_POLICY).length > 0) return this.set({ error: "password-rules" });
    const gen = this.generation;
    this.set({ busy: true });
    try {
      await this.deps.api.confirmForgotPassword(this.s.email, code, password, (await this.context(this.s.email)).contextData);
    } catch (err) {
      if (gen !== this.generation) return;
      return this.set({ busy: false, error: classifyEmailCode(err) });
    }
    if (gen !== this.generation) return;
    // The pool's Post confirmation trigger has stamped and signed the person out everywhere; they sign in afresh here.
    this.set({ busy: false, step: { name: "email" }, factors: ["WEB_AUTHN", "PASSWORD"], notice: "password-changed" });
  }

  /** The emailed code that confirms the signed-in person's address (VerifyUserAttribute), then on into the product. */
  async submitEmailCode(typed: string): Promise<void> {
    const pending = this.pending;
    if (this.s.busy || this.s.step.name !== "verify-email" || !pending) return;
    const code = typed.replace(/[\s-]/g, "");
    if (!/^\d{6}$/.test(code)) return this.set({ error: "email-code-format" });
    const gen = this.generation;
    this.set({ busy: true });
    try {
      await this.deps.api.verifyEmail(pending.tokens.AccessToken ?? "", code);
    } catch (err) {
      if (gen !== this.generation) return;
      return this.set({ busy: false, error: classifyEmailCode(err) });
    }
    if (gen !== this.generation) return;
    await this.afterEmail(pending, gen);
  }

  /** "Send a new code" on the email step, and the first send on arriving there. */
  async resendEmailCode(): Promise<void> {
    const pending = this.pending;
    if (this.s.step.name !== "verify-email" || !pending) return;
    const first = !this.s.step.sent;
    const gen = this.generation;
    this.set({ busy: true });
    try {
      await this.deps.api.sendEmailVerificationCode(pending.tokens.AccessToken ?? "");
    } catch (err) {
      if (gen !== this.generation) return;
      return this.set({ busy: false, step: { name: "verify-email", sent: !first }, error: classifyEmailCode(err) });
    }
    if (gen !== this.generation) return;
    this.set({ busy: false, step: { name: "verify-email", sent: true }, ...(first ? {} : { notice: "code-resent" as const }) });
  }

  /** "Not now": the address stays unverified (a reset needs a verified address, so it is asked again next time). */
  async skipEmailVerification(): Promise<void> {
    const pending = this.pending;
    if (this.s.busy || this.s.step.name !== "verify-email" || !pending) return;
    const gen = this.generation;
    this.set({ busy: true });
    await this.afterEmail(pending, gen);
  }

  /** After the address step: the passkey offer for a first sign-in that set up its authenticator, else in. */
  private async afterEmail(result: SignedIn, gen: number): Promise<void> {
    if (this.authenticatorSetUp && this.deps.enrollPasskey && this.deps.passkeysPossible()) {
      this.pending = result;
      return this.set({ busy: false, step: { name: "passkey-offer" } });
    }
    await this.handOver(result, gen);
  }

  private async afterFirstFactor(answer: AuthAnswer, gen: number): Promise<void> {
    if (answer.AuthenticationResult) return this.finish(answer.AuthenticationResult, gen);
    if (answer.ChallengeName === "SOFTWARE_TOKEN_MFA" && answer.Session) return this.set({ busy: false, step: { name: "code", session: answer.Session } });
    // A signed-in person is past first sign-in: a step-up that meets its challenges takes them nowhere.
    if (this.deps.stepUp && (answer.ChallengeName === "NEW_PASSWORD_REQUIRED" || answer.ChallengeName === "MFA_SETUP")) return this.set({ busy: false, step: { name: "password" }, error: "unexpected" });
    if (answer.ChallengeName === "NEW_PASSWORD_REQUIRED" && answer.Session) return this.set({ busy: false, step: { name: "new-password", session: answer.Session } });
    if (answer.ChallengeName === "MFA_SETUP" && answer.Session) return this.associate(answer.Session, gen);
    // SELECT_CHALLENGE in answer to a password is Cognito's reply for an address with no account (R10), and anything
    // else is a step this screen does not take: either way, the same words as a wrong password. A step-up is for a
    // person who exists, whose wrong password is NotAuthorizedException, so there any challenge it does not take is
    // said as something that went wrong — in words in the dialog, never a stall (gairm/lms#397).
    this.set({ busy: false, step: { name: "password" }, error: this.deps.stepUp ? "unexpected" : "wrong-password" });
  }

  private async runPasskey(answer: AuthAnswer, gen: number): Promise<void> {
    this.set({ busy: true, step: { name: "passkey" } });
    let credential: string;
    const prompt = new AbortController();
    this.prompt = prompt;
    try {
      credential = await this.deps.passkeyAssertion(answer.ChallengeParameters?.["CREDENTIAL_REQUEST_OPTIONS"], prompt.signal);
    } catch (err) {
      if (gen !== this.generation) return;
      return this.set({ busy: false, step: { name: "choose" }, error: classify(err, "passkey") });
    }
    if (gen !== this.generation) return;
    try {
      const result = await this.respond("WEB_AUTHN", answer.Session ?? "", { USERNAME: this.s.email, CREDENTIAL: credential }, (await this.context(this.s.email)).contextData);
      if (gen !== this.generation) return;
      if (result.AuthenticationResult) return await this.finish(result.AuthenticationResult, gen);
      this.set({ busy: false, step: { name: "choose" }, error: "passkey-failed" });
    } catch (err) {
      if (gen !== this.generation) return;
      const error = classify(err, "passkey");
      this.set({ busy: false, step: { name: "choose" }, error: error === "wrong-password" ? "passkey-failed" : error });
    }
  }

  /**
   * Confirms the device the sign-in was issued with, then hands everything to the session layer. On a pool that tracks
   * devices the refresh token renews only for a confirmed device (session-store.ts), so a sign-in whose device cannot be
   * confirmed is not kept: it would end at its first renewal, fifteen minutes in. One retry, a confirmation that landed
   * unseen waited for (confirmNewDevice, gairm/lms#431), then the person is told.
   */
  private async finish(tokens: FreshTokens, gen: number): Promise<void> {
    this.set({ busy: true, step: { name: "finishing" } });
    // A step-up's tokens replace the session's ID and access tokens and nothing else (auth.ts replaceTokens): its device,
    // if Cognito issued one, is never confirmed, because nothing renews with it — the session keeps its own.
    if (this.deps.stepUp) return this.handOver({ tokens }, gen);
    let device: DeviceSlot | undefined;
    let rememberedNow = false;
    const meta = tokens.NewDeviceMetadata;
    if (meta?.DeviceKey && meta.DeviceGroupKey && tokens.AccessToken) {
      const verifier = await this.deps.deviceVerifier(meta.DeviceGroupKey, meta.DeviceKey);
      const confirmed = await this.confirmNewDevice(tokens.AccessToken, tokens.RefreshToken, meta.DeviceKey, verifier, gen);
      if (gen !== this.generation) return;
      if (!confirmed) return this.set({ busy: false, step: { name: "email" }, error: "device" });
      device = { key: meta.DeviceKey, groupKey: meta.DeviceGroupKey, password: verifier.password, confirmed: true };
      if (this.s.remember && (await this.remember(tokens.AccessToken, device))) device.remembered = rememberedNow = true;
      if (gen !== this.generation) return;
    } else if (this.device && tokens.AccessToken) {
      // A sign-in that sent a device's key: Cognito issues no new device, and the refresh token renews with this one's key.
      device = { key: this.device.key, groupKey: this.device.groupKey, password: this.device.password, confirmed: true };
      if (this.deviceRemembered) device.remembered = true;
      else if (this.s.remember && (await this.remember(tokens.AccessToken, device))) device.remembered = rememberedNow = true;
      if (gen !== this.generation) return;
    }
    const result: SignedIn = { tokens, ...(device ? { device } : {}), ...(rememberedNow ? { rememberedNow } : {}) };
    if (this.authenticatorSetUp && tokens.AccessToken) {
      // The MFA_SETUP answer leaves the person's MFA preference unset (pitfall 5; lms-dev, 2026-10-01: GetUser lists
      // none), as Account › Security's enrollment does not. Best effort: the pool requires MFA either way.
      await this.deps.api.setTotpPreference(tokens.AccessToken).catch(() => undefined);
      if (gen !== this.generation) return;
    }
    // An unverified address (gairm/lms#394) cannot receive a reset code: confirm it now, or Not now.
    if (tokens.AccessToken && !(this.deps.emailVerified ?? emailVerifiedInToken)(tokens, this.s.email)) {
      this.pending = result;
      this.set({ busy: false, step: { name: "verify-email", sent: false } });
      return this.resendEmailCode();
    }
    await this.afterEmail(result, gen);
  }

  /**
   * ConfirmDevice for the device this sign-in was issued, told apart from a confirmation that landed unseen (gairm/lms#431).
   * True when the refresh token renews with the key — the only thing a confirmed device is for.
   *
   * Cognito sometimes takes far longer than the client's 10 s to answer ConfirmDevice (lms-dev, 2 Oct: 28 s once, past
   * the timeout once, both right after Sign out everywhere had forgotten the person's devices). The request has landed:
   * the retry is refused `InvalidParameterException` "Invalid device key given." — Cognito's answer to confirming a
   * device a second time (probed on lms-dev, 2 Oct) — while a renewal with the key is still refused "Invalid Refresh
   * Token.", Cognito's answer for a device not yet confirmed. So a confirmation that timed out, failed on the network or
   * is refused as a repeat is waited for: the renewal is asked again over about half a minute, with the screen saying it
   * is taking longer. Refused for any other reason, it is asked once. Never confirmed: false, and the screen says so.
   */
  private async confirmNewDevice(accessToken: string, refreshToken: string | undefined, deviceKey: string, verifier: DeviceVerifier, gen: number): Promise<boolean> {
    let unseen = false;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await this.deps.api.confirmDevice(accessToken, deviceKey, verifier.config, this.deps.deviceName(), CONFIRM_DEVICE_TIMEOUT_MS);
        return true;
      } catch (err) {
        if (gen !== this.generation) return false;
        if (mayHaveLanded(err)) {
          unseen = true;
          this.set({ step: { name: "finishing", slow: true } });
        }
        // A repeat is refused at once, so trying again would only be refused again: wait for the first instead.
        if (confirmedAlready(err)) break;
      }
    }
    if (!refreshToken) return false;
    const renews = () => this.deps.api.getTokensFromRefreshToken(refreshToken, deviceKey, CONFIRM_DEVICE_TIMEOUT_MS).then((r) => !!r.AuthenticationResult, () => false);
    if (await renews()) return true;
    if (!unseen) return false;
    const sleep = this.deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    for (const ms of CONFIRM_SETTLE_MS) {
      await sleep(ms);
      if (gen !== this.generation) return false;
      if (await renews()) return true;
    }
    return false;
  }

  /**
   * "Remember this device": UpdateDeviceStatus remembered, then the device kept for this address. True when both held. A
   * refusal is not the person's problem now: they are signed in, and the next sign-in asks for the code.
   */
  private async remember(accessToken: string, device: DeviceSlot): Promise<boolean> {
    try {
      await this.deps.api.updateDeviceStatus(accessToken, device.key, "remembered");
    } catch {
      return false;
    }
    if (!device.password || !this.deps.rememberedDevices) return false;
    try {
      this.deps.rememberedDevices.write(this.s.email, { key: device.key, groupKey: device.groupKey, password: device.password });
      return true;
    } catch {
      return false;
    }
  }

  /** The tokens and the device to the session layer, which leaves this screen. */
  private async handOver(result: SignedIn, gen: number): Promise<void> {
    this.pending = undefined;
    try {
      await this.deps.complete(result);
    } catch {
      if (gen !== this.generation) return;
      return this.set({ busy: false, step: this.home(), error: "unexpected" });
    }
    if (gen !== this.generation) return;
    this.set({ busy: false, step: { name: "done" } });
  }

  private async context(username: string): Promise<{ contextData?: UserContextData }> {
    try {
      const c = await this.deps.contextData(username);
      return c ? { contextData: c } : {};
    } catch {
      return {};
    }
  }
}

/** The ID token's `email_verified`, read without verification (the API verifies); unreadable counts as verified. */
export function emailVerifiedInToken(tokens: FreshTokens): boolean {
  try {
    const payload = (tokens.IdToken ?? "").split(".")[1];
    if (!payload) return true;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { email_verified?: unknown };
    return !(claims.email_verified === false || claims.email_verified === "false");
  } catch {
    return true;
  }
}

/** A reset's or an address confirmation's code, refused, as we say it. */
export function classifyEmailCode(err: unknown): SignInErrorCode {
  if (err instanceof CognitoApiError) {
    switch (err.code) {
      case "CodeMismatchException":
        return "email-code-wrong";
      case "ExpiredCodeException":
        return "email-code-expired";
      case "InvalidPasswordException":
        return "password-rejected";
      case "LimitExceededException":
      case "TooManyRequestsException":
      case "TooManyFailedAttemptsException":
        return "code-rate-limited";
      case "TimeoutError":
        return "network";
      default:
        return "unexpected";
    }
  }
  if (err instanceof TypeError) return "network";
  return "unexpected";
}

/** What the finishing step says while a device confirmation is waited for (gairm/lms#431). */
export const FINISHING_SLOW_WORDS = "This is taking longer than usual. Keep this page open. It can take up to a minute.";

/** How long ConfirmDevice, and each renewal that checks it, waits for Cognito (gairm/lms#425). */
export const CONFIRM_DEVICE_TIMEOUT_MS = 10_000;
/**
 * The pauses between renewals while a confirmation that landed unseen settles (gairm/lms#431): about half a minute in
 * all, past the 28 s the slowest one took on lms-dev.
 */
export const CONFIRM_SETTLE_MS: readonly number[] = [2_000, 3_000, 5_000, 5_000, 5_000, 5_000, 5_000];

/** A ConfirmDevice that got no answer (a timeout, the network), or was refused as a repeat: it may have landed at Cognito. */
function mayHaveLanded(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof CognitoApiError && err.code === "TimeoutError") || confirmedAlready(err);
}

/** Cognito's answer to confirming a device that is already confirmed (lms-dev, 2 Oct): a confirmation went through. */
function confirmedAlready(err: unknown): boolean {
  return err instanceof CognitoApiError && err.code === "InvalidParameterException" && /invalid device key/i.test(err.message);
}

/** The first factors Cognito offers this person, as this screen names them; SRP is not a factor this screen runs. */
function factorsOf(answer: AuthAnswer): Factor[] {
  const offered = answer.AvailableChallenges ?? [];
  const f: Factor[] = [];
  if (offered.includes("WEB_AUTHN")) f.push("WEB_AUTHN");
  if (offered.includes("PASSWORD") || offered.includes("PASSWORD_SRP") || offered.length === 0) f.push("PASSWORD");
  return f;
}

/** A name for the device list: the browser and the platform, from the user agent, never anything about the person. */
export function deviceNameFromUserAgent(ua: string): string {
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "A browser";
  const platform = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return platform ? `${browser} on ${platform}` : browser;
}
